import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';

import { Download, RefreshCw, Search, Shield } from 'lucide-react';

const API = require('../services/apiBase').default || require('../services/apiBase');

const GLViewer = ({ glId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [adv, setAdv] = useState({ account_id: '', acc1: '', acc2: '', org: '', org4: '', org5: '' });
  const [showValidation, setShowValidation] = useState(false);
  const [validationData, setValidationData] = useState(null);

  const loadColumns = async () => {
    setError(null);
    try {
      const res = await fetch(`${API}/gl/columns/${glId}`);
      if (res.ok) { 
        const data = await res.json(); 
        // Filter out the last column (likely an internal ID or unwanted field)
        const cols = (data.columns || []).slice(0, -1);
        setColumns(cols);
      }
    } catch (e) { setError(e.message || 'Failed to load columns'); }
  };

  const loadPage = async (p = page, s = pageSize) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), page_size: String(s) });
      if (adv.account_id) params.set('account_id', adv.account_id);
      if (adv.acc1) params.set('account_id_level_1', adv.acc1);
      if (adv.acc2) params.set('account_id_level_2', adv.acc2);
      if (adv.org) params.set('organization_id', adv.org);
      if (adv.org4) params.set('organizational_id_level_4', adv.org4);
      if (adv.org5) params.set('organizational_id_level_5', adv.org5);
      const res = await fetch(`${API}/gl/rows/${glId}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []); setTotal(data.total || 0); setPage(data.page || p); setPageSize(data.page_size || s);
    } catch (e) { setError(e.message || 'Failed to load rows'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (glId) { loadColumns(); loadPage(1, pageSize); } }, [glId]);

  const filteredRows = useMemo(() => {
    if (!filter.trim()) return rows;
    const s = filter.toLowerCase();
    return rows.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(s)));
  }, [rows, filter]);

  const exportCsv = async () => {
    try { const res = await fetch(`${API}/gl/export/${glId}`); if (!res.ok) throw new Error(`HTTP ${res.status}`); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `gl_${glId}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);} catch(e) { setError(e.message || 'Export failed'); }
  };

  return (
    <div className="pro-card">
      <div className="pro-card-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 className="pro-card-title">General Ledger</h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
              {total.toLocaleString()} total rows
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="pro-btn pro-btn-secondary" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }} onClick={() => loadPage(1, pageSize)}>
              <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
              Refresh
            </button>
            <button className="pro-btn pro-btn-secondary" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }} onClick={exportCsv}>
              <Download style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
              Export CSV
            </button>
          </div>
        </div>
      </div>
      <div className="pro-card-content">
        {error && (
          <div style={{ padding: '0.75rem', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 'var(--radius-md)', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: '#991B1B' }}>{error}</p>
          </div>
        )}

        {/* Enhanced Filter Panel */}
        <div className="filter-panel">
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              FILTERS
            </label>
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ position: 'relative' }}>
              <Input 
                placeholder="Search in current page..." 
                value={filter} 
                onChange={e=>setFilter(e.target.value)}
                className="pro-input"
              />
              <Search style={{ width: '1rem', height: '1rem', color: 'var(--gray-400)', position: 'absolute', right: '0.75rem', top: '0.625rem', pointerEvents: 'none' }} />
            </div>
          </div>

          <div className="filter-grid">
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Account ID
              </label>
              <Input 
                placeholder="e.g., 110-100-*" 
                value={adv.account_id} 
                onChange={e=>setAdv(prev=>({...prev, account_id: e.target.value}))}
                className="pro-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Account Level 1
              </label>
              <Input 
                placeholder="e.g., 5XX" 
                value={adv.acc1} 
                onChange={e=>setAdv(prev=>({...prev, acc1: e.target.value}))}
                className="pro-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Account Level 2
              </label>
              <Input 
                placeholder="e.g., 100" 
                value={adv.acc2} 
                onChange={e=>setAdv(prev=>({...prev, acc2: e.target.value}))}
                className="pro-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Organization ID
              </label>
              <Input 
                placeholder="Organization ID" 
                value={adv.org} 
                onChange={e=>setAdv(prev=>({...prev, org: e.target.value}))}
                className="pro-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Org Level 4
              </label>
              <Input 
                placeholder="Org ID Level 4" 
                value={adv.org4} 
                onChange={e=>setAdv(prev=>({...prev, org4: e.target.value}))}
                className="pro-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.25rem' }}>
                Org Level 5
              </label>
              <Input 
                placeholder="e.g., 701" 
                value={adv.org5} 
                onChange={e=>setAdv(prev=>({...prev, org5: e.target.value}))}
                className="pro-input"
              />
            </div>
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="pro-btn pro-btn-primary" onClick={()=>loadPage(1, pageSize)}>
              <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              Apply Filters
            </button>
          </div>
        </div>

        {/* Inline Validation Display */}
        {showValidation && validationData && (
          <div className="fade-in" style={{ marginBottom: '1.5rem' }}>
            <div className="pro-card" style={{ border: '2px solid var(--accent-orange)' }}>
              <div className="pro-card-header" style={{ background: '#FFF7ED', borderBottom: '1px solid var(--accent-orange)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                    Validation Results
                  </h3>
                  <button 
                    onClick={() => setShowValidation(false)}
                    style={{ color: 'var(--gray-500)', cursor: 'pointer', background: 'none', border: 'none', fontSize: '1.25rem' }}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="pro-card-content">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="pro-stat-card">
                    <div className="pro-stat-value" style={{ color: validationData.coverage_percent >= 100 ? 'var(--success-green)' : 'var(--warning-amber)' }}>
                      {validationData.coverage_percent}%
                    </div>
                    <div className="pro-stat-label">Coverage Rate</div>
                  </div>
                  <div className="pro-stat-card">
                    <div className="pro-stat-value" style={{ color: validationData.unmapped_accounts.length > 0 ? 'var(--warning-amber)' : 'var(--success-green)' }}>
                      {validationData.unmapped_accounts.length}
                    </div>
                    <div className="pro-stat-label">Unmapped Accounts</div>
                  </div>
                </div>

                {validationData.unmapped_accounts.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.75rem' }}>
                      Unmapped Account Details
                    </h4>
                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)' }}>
                      <table className="financial-table">
                        <thead>
                          <tr>
                            <th>Account Number</th>
                            <th>Description</th>
                            <th style={{ textAlign: 'right' }}>Total Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validationData.unmapped_accounts.slice(0, 10).map((acc, i) => (
                            <tr key={i}>
                              <td style={{ fontFamily: 'monospace' }}>{acc.account || 'N/A'}</td>
                              <td>{acc.description || 'No description'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                                ${(acc.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {validationData.unmapped_accounts.length > 10 && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.5rem' }}>
                        Showing 10 of {validationData.unmapped_accounts.length} unmapped accounts
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Professional Data Table */}
        <div style={{ overflowX: 'auto', marginBottom: '1rem', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
          <table className="financial-table" style={{ margin: 0, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
            <thead>
              <tr>
                {columns.filter(c => c && c.trim()).map((c, idx) => (
                  <th key={c} style={{ 
                    padding: '1rem',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--gray-700)',
                    background: 'linear-gradient(to bottom, #F9FAFB, #F3F4F6)',
                    borderBottom: '2px solid var(--gray-300)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    whiteSpace: 'nowrap',
                    textAlign: idx === 0 ? 'left' : 'right'
                  }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => {
                const visibleColumns = columns.filter(c => c && c.trim());
                return (
                  <tr key={i} style={{ 
                    transition: 'background-color 0.15s ease',
                    borderBottom: i === filteredRows.length - 1 ? 'none' : '1px solid var(--gray-200)'
                  }}>
                    {visibleColumns.map((c, idx) => {
                      const val = r[c];
                      let displayVal = '';
                      let isCurrency = false;
                      
                      if (val === null || val === undefined) {
                        displayVal = '—';
                      } else if (typeof val === 'number') {
                        // Check if this is likely a currency column
                        isCurrency = c.toLowerCase().includes('amount') || c.toLowerCase().includes('balance');
                        
                        // Format numbers appropriately
                        if (Math.abs(val) < 0.001 && val !== 0) {
                          // Very small numbers - show more precision
                          displayVal = val.toFixed(8);
                        } else if (Math.abs(val) < 1 && !isCurrency) {
                          // Small decimals (not currency)
                          displayVal = val.toFixed(4);
                        } else {
                          // Regular numbers with proper formatting
                          const formatted = val.toLocaleString(undefined, { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          });
                          displayVal = isCurrency ? `$${formatted}` : formatted;
                        }
                      } else {
                        displayVal = String(val);
                      }
                      
                      return (
                        <td key={c} style={{ 
                          padding: '1rem',
                          fontSize: '0.875rem',
                          color: 'var(--gray-900)',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                          textAlign: idx === 0 ? 'left' : 'right',
                          whiteSpace: idx === 0 ? 'nowrap' : 'normal',
                          fontVariantNumeric: 'tabular-nums'
                        }}>
                          {displayVal}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {filteredRows.length===0 && (
                <tr>
                  <td colSpan={columns.filter(c => c && c.trim()).length || 1} style={{ 
                    textAlign: 'center', 
                    color: 'var(--gray-500)', 
                    padding: '3rem',
                    fontSize: '0.875rem'
                  }}>
                    No rows found matching your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Professional Pagination */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'var(--gray-50)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--gray-200)'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '500' }}>
            Page <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>{page}</span> of <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>{Math.max(1, Math.ceil(total / pageSize))}</span>
            <span style={{ marginLeft: '1rem', color: 'var(--gray-500)' }}>•</span>
            <span style={{ marginLeft: '1rem' }}>
              Showing <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>{filteredRows.length}</span> of <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>{pageSize}</span> rows
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className="pro-btn pro-btn-secondary" 
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', fontWeight: '500' }}
              disabled={page<=1} 
              onClick={()=>loadPage(page-1, pageSize)}
            >
              ← Previous
            </button>
            <button 
              className="pro-btn pro-btn-secondary" 
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', fontWeight: '500' }}
              disabled={(page * pageSize) >= total} 
              onClick={()=>loadPage(page+1, pageSize)}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GLViewer;
